// GOV.UK-pattern cookie consent banner.
//
// UK PECR requires informed, unbundled consent for non-essential cookies.
// This banner follows the GOV.UK Design System pattern:
//   https://design-system.service.gov.uk/components/cookie-banner/
//
// Behaviour:
//   - Shows at bottom of screen on first visit (no prior choice).
//   - Two equal-weight buttons: Accept / Reject. No dark patterns.
//   - After choice: shows a brief confirmation message with "Hide" button.
//   - Essential cookies (session, JWT) are never mentioned — they're exempt.
//
// Props:
//   consent   — "accepted" | "rejected" | null  (from useCookieConsent)
//   onAccept  — () => void
//   onReject  — () => void
//   onReset   — () => void  (returns to null so user can change their choice)

import { useEffect, useRef, useState } from "react";
import { C } from "../theme.js";

// Layout contract: this banner publishes its own pixel height to the CSS
// custom property `--nl-bottom-inset` on the document root whenever it is
// visible. Any element that needs to avoid sitting under the banner can
// reserve space with `paddingBottom: var(--nl-bottom-inset, 0px)`.
//
// Why a CSS var instead of a fixed offset:
// The banner has two heights — the tall accept/reject prompt (~180px) and
// the short confirmation strip (~70px) — and the height also changes as
// text wraps on narrow screens. A static padding number would either over-
// reserve space (ugly gap) or under-reserve (CTAs hidden).

const BOTTOM_INSET_VAR = "--nl-bottom-inset";

function setBottomInset(px) {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(BOTTOM_INSET_VAR, `${px}px`);
}

// sessionStorage key — "Hide" persists for the rest of the browsing session,
// so the confirmation strip doesn't reappear after every reload.
const HIDDEN_KEY = "nomloop_cookie_banner_hidden";

export function CookieBanner({ consent, onAccept, onReject, onReset }) {
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem(HIDDEN_KEY) === "1"; } catch { return false; }
  });
  const rootRef = useRef(null);
  const visible = !hidden;
  const hide = () => {
    try { sessionStorage.setItem(HIDDEN_KEY, "1"); } catch {}
    setHidden(true);
  };

  // Publish height to the CSS var while visible, clear it on unmount/hide so
  // nothing else is left reserving space.
  useEffect(() => {
    if (!visible) {
      setBottomInset(0);
      return;
    }
    const el = rootRef.current;
    if (!el) return;
    const measure = () => setBottomInset(el.offsetHeight || 0);
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      ro?.disconnect();
      setBottomInset(0);
    };
  }, [visible, consent]);

  // Nothing to show if user already chose or manually dismissed confirmation.
  if (hidden || (consent !== null && consent !== "accepted" && consent !== "rejected")) return null;
  if (hidden) return null;

  // ── Confirmation message (shown after a choice) ────────────────────────────
  if (consent !== null) {
    return (
      <div
        ref={rootRef}
        role="alert"
        aria-live="polite"
        style={{
          // z-index 350 sits ABOVE the bottom nav (z-200) but BELOW any modal
          // (z-500+). Previously this was 9999, which covered the meal-detail
          // modal's "Add to this week" CTA — beta-blocker.
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 350,
          background: C.white,
          borderTop: `4px solid ${C.green}`,
          padding: "16px 20px",
          boxShadow: "0 -4px 24px rgba(0,0,0,0.08)",
        }}
      >
        <div style={{ maxWidth: 680, margin: "0 auto", display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <p style={{ flex: 1, fontSize: 14, color: C.charcoal, lineHeight: 1.5, margin: 0 }}>
            {consent === "accepted"
              ? "You've accepted analytics cookies."
              : "You've rejected analytics cookies."}
          </p>
          <div style={{ display:"flex", gap:8, flexShrink:0 }}>
            {onReset && (
              <button
                onClick={onReset}
                style={{
                  padding: "8px 16px", borderRadius: 8, border: `1.5px solid ${C.border}`,
                  background: C.white, color: C.grey,
                  fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
                }}
              >
                Change
              </button>
            )}
            <button
              onClick={hide}
              style={{
                padding: "8px 16px", borderRadius: 8, border: `1.5px solid ${C.border}`,
                background: C.white, color: C.charcoal,
                fontWeight: 700, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              Hide
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Initial banner (no choice yet) ────────────────────────────────────────
  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label="Cookie consent"
      aria-describedby="cookie-banner-desc"
      style={{
        // See z-index note on the confirmation variant above.
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 350,
        background: C.white,
        borderTop: `4px solid ${C.green}`,
        padding: "20px 20px 24px",
        boxShadow: "0 -4px 32px rgba(0,0,0,0.10)",
      }}
    >
      <div style={{ maxWidth: 680, margin: "0 auto" }}>
        <p style={{ fontWeight: 700, fontSize: 15, color: C.charcoal, marginBottom: 6 }}>
          Cookies on NomLoop
        </p>
        <p
          id="cookie-banner-desc"
          style={{ fontSize: 14, color: C.grey, lineHeight: 1.6, marginBottom: 16 }}
        >
          We use essential cookies to keep the app working. We'd also like to use
          analytics cookies so we can understand how you use NomLoop and make improvements.
          You can accept or reject analytics cookies below. Essential cookies cannot be turned off.
        </p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button
            onClick={onAccept}
            style={{
              padding: "11px 22px", borderRadius: 8, border: "none",
              background: C.green, color: C.white,
              fontWeight: 700, fontSize: 14, cursor: "pointer",
            }}
          >
            Accept analytics cookies
          </button>
          <button
            onClick={onReject}
            style={{
              padding: "11px 22px", borderRadius: 8,
              border: `1.5px solid ${C.border}`,
              background: C.white, color: C.charcoal,
              fontWeight: 700, fontSize: 14, cursor: "pointer",
            }}
          >
            Reject analytics cookies
          </button>
        </div>
        <p style={{ marginTop: 12, fontSize: 12, color: C.greyLight }}>
          <a href="/privacy" style={{ color: C.green }}>View our privacy policy</a> ·{" "}
          <a href="/cookies" style={{ color: C.green }}>Cookie policy</a>
        </p>
      </div>
    </div>
  );
}
